import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

export interface OnboardingStep {
  id: string;
  name: string;
  description: string;
  required: boolean;
  completed: boolean;
  order: number;
}

export interface OnboardingProgress {
  id: number;
  user_id: number;
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  steps_completed: string[];
  current_step: string | null;
  user_context: Record<string, unknown>;
  show_hints: boolean;
  onboarding_conversation_id: number | null;
  started_at: string | null;
  completed_at: string | null;
  steps: OnboardingStep[];
  progress_percent: number;
}

export interface OnboardingAction {
  type: 'navigate' | 'highlight' | 'create_agent' | 'complete_step' | 'get_user_context' | 'show_checklist';
  page?: string;
  url?: string;
  element_id?: string;
  message?: string;
  name?: string;
  instructions?: string;
  step_id?: string;
  expanded?: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useOnboarding() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightedElement, setHighlightedElement] = useState<string | null>(null);
  const [highlightMessage, setHighlightMessage] = useState<string | null>(null);
  const [showChecklist, setShowChecklist] = useState(false);

  // Fetch progress on mount
  const fetchProgress = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<OnboardingProgress>('/onboarding/progress');
      setProgress(response.data);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch onboarding progress';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Listen for step-completed events from other components
  // Note: Don't listen to onboarding:reset here - the reset() function already updates progress
  // and calling fetchProgress() would trigger backend auto-check which re-completes steps
  useEffect(() => {
    const handleStepCompleted = () => {
      fetchProgress();
    };
    window.addEventListener('onboarding:step-completed', handleStepCompleted);
    return () => {
      window.removeEventListener('onboarding:step-completed', handleStepCompleted);
    };
  }, [fetchProgress]);

  // Complete a step
  const completeStep = useCallback(async (stepId: string) => {
    try {
      const response = await api.post<OnboardingProgress>(`/onboarding/progress/step/${stepId}`);
      setProgress(response.data);
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to complete step';
      setError(message);
      throw err;
    }
  }, []);

  // Update context
  const updateContext = useCallback(async (context: Record<string, unknown>) => {
    try {
      const response = await api.put<OnboardingProgress>('/onboarding/progress/context', { context });
      setProgress(response.data);
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update context';
      setError(message);
      throw err;
    }
  }, []);

  // Dismiss onboarding
  const dismiss = useCallback(async () => {
    try {
      const response = await api.post<OnboardingProgress>('/onboarding/dismiss');
      setProgress(response.data);
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to dismiss onboarding';
      setError(message);
      throw err;
    }
  }, []);

  // Reset onboarding
  const reset = useCallback(async () => {
    try {
      const response = await api.post<OnboardingProgress>('/onboarding/reset');
      setProgress(response.data);
      // Emit event to notify other components (e.g., OnboardingWidget)
      window.dispatchEvent(new CustomEvent('onboarding:reset'));
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to reset onboarding';
      setError(message);
      throw err;
    }
  }, []);

  // Toggle hints
  const toggleHints = useCallback(async (showHints: boolean) => {
    try {
      const response = await api.put<OnboardingProgress>('/onboarding/progress/hints', { show_hints: showHints });
      setProgress(response.data);
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to toggle hints';
      setError(message);
      throw err;
    }
  }, []);

  // Handle MCP action from Totem Guide
  const handleAction = useCallback((action: OnboardingAction) => {
    switch (action.type) {
      case 'navigate':
        if (action.url) {
          navigate(action.url);
        }
        break;
      
      case 'highlight':
        if (action.element_id) {
          setHighlightedElement(action.element_id);
          setHighlightMessage(action.message || null);
          // Auto-clear after 5 seconds
          setTimeout(() => {
            setHighlightedElement(null);
            setHighlightMessage(null);
          }, 5000);
        }
        break;
      
      case 'complete_step':
        if (action.step_id) {
          completeStep(action.step_id);
        }
        break;
      
      case 'show_checklist':
        setShowChecklist(action.expanded !== false);
        break;
      
      default:
        console.log('Unknown onboarding action:', action);
    }
  }, [navigate, completeStep]);

  // Clear highlight
  const clearHighlight = useCallback(() => {
    setHighlightedElement(null);
    setHighlightMessage(null);
  }, []);

  return {
    // State
    progress,
    loading,
    error,
    highlightedElement,
    highlightMessage,
    showChecklist,
    
    // Computed
    isActive: progress?.status === 'in_progress' || progress?.status === 'not_started',
    isCompleted: progress?.status === 'completed',
    isSkipped: progress?.status === 'skipped',
    shouldShowWidget: progress?.status !== 'completed' && progress?.status !== 'skipped',
    
    // Actions
    fetchProgress,
    completeStep,
    updateContext,
    dismiss,
    reset,
    toggleHints,
    handleAction,
    clearHighlight,
    setShowChecklist,
  };
}

export default useOnboarding;
